<template>
      <div>
            <j-card class="bg5">
                  <j-expendable-box>
                        <!-- Headline -->
                        <div class="xs-12 flex xs-middle xs-between fs4 fw7 c2 p4" slot="header">
                              <span>{{ name }}</span>
                              <span class="c6 fs3 pr4">{{ computedCreatedAt }}</span>
                        </div>

                        <!-- Body -->
                        <div slot="body" class="bg4">
                              <div class="xs-12 p4">
                                    <!-- Product -->
                                    <product-info :product="product" />

                                    <!-- Premium with discounts and loadings -->
                                    <premium-info
                                          :policyId="policyId"
                                          :offerId="offerId"
                                          :productId="product.productId"
                                          :pricing="pricing"
                                          :hasLoadingsActions="true"
                                          :hasDiscountActions="true"
                                          :hasPremiumActions="true"
                                          @loading:created="$emit('loading:created')"
                                          @loading:deleted="$emit('loading:deleted')"
                                          @discount:created="$emit('discount:created')"
                                          @discount:deleted="$emit('discount:deleted')"
                                          @premium:updated="$emit('premium:updated')"
                                          class="mt6"
                                    />

                                    <!-- Exclusions -->
                                    <exclusions-info
                                          :policyId="policyId"
                                          :offerId="offerId"
                                          :productId="product.productId"
                                          :list="exclusions"
                                          :hasActions="true"
                                          @deleted="$emit('exclusion:created')"
                                          @created="$emit('exclusion:deleted')"
                                          class="mt6"
                                    />

                                    <!-- Benefit Options -->
                                    <quote-benefit-options :list="computedBenefitOptions" class="mt6" />

                                    <!-- Actions -->
                                    <offer-actions
                                          :policyId="policyId"
                                          :offerId="offerId"
                                          :context="{
                                            pricing,
                                            product,
                                            exclusions
                                          }"
                                          class="mt6"
                                    />

                                    <!-- Footer menu -->
                                    <div
                                          class="flex wrap xs-12 xs-between bt1 bc7 pt2 mt8"
                                          v-can="'writeQuotes'"
                                    >
                                          <!-- Duplicate Offer -->
                                          <j-button
                                                size="small"
                                                @click="$emit('duplicate')"
                                                v-can="'writeQuotes'"
                                          >{{ this.$t("copy_offer") }}</j-button>

                                          <!-- Delete Offer -->
                                          <j-button
                                                type="error"
                                                size="small"
                                                v-if="allowDelete"
                                                @click="deleteOffer"
                                                :isLoading="isDeleteLoading"
                                                action="deleteOffer"
                                          >{{ this.$t("delete_offer") }}</j-button>
                                    </div>
                              </div>
                        </div>
                  </j-expendable-box>
            </j-card>
      </div>
</template>

<script>
import JCard from "@/components/JCard/"
import JExpendableBox from "@/components/JExpendableBox/"
import JButton from "@/components/JButton/"
import ProductInfo from "../components/ProductInfo"
import PremiumInfo from "../components/PremiumInfo"
import ExclusionsInfo from "../components/ExclusionsInfo"
import OfferActions from "../components/OfferActions"
import AddDiscount from "../components/AddDiscount"
import QuoteBenefitOptions from "./QuoteBenefitOptions"
import api from "../api/"
import dayjs from "dayjs"

export default {
	components: {
		JCard,
		JExpendableBox,
		JButton,
		ProductInfo,
		PremiumInfo,
		ExclusionsInfo,
		OfferActions,
		AddDiscount,
		QuoteBenefitOptions
	},

	props: {
		name: {
			type: String,
			required: true,
			default: ""
		},

		policyId: {
			type: String,
			required: true,
			default: ""
		},

		offerId: {
			type: String,
			required: true,
			default: ""
		},

		createdAt: {
			validator(val) {
				return val === null || typeof val === "string"
			},
			required: true,
			default: null
		},

		allowDelete: {
			type: Boolean,
			required: false,
			default: true
		},

		/**
		 * Pricing type we get from graphql policies.pricing
		 */
		pricing: {
			type: Object,
			required: true,
			default: () => ({
				amount: null,
				currencyCode: null,
				formattedPrice: null,
				originalPrice: null,
				formattedOriginalPrice: null,
				appliedDiscounts: {
					code: null,
					ratio: null,
					order: null
				},
				loadings: {
					id: null,
					codeName: {
						code: null,
						name: null
					},
					ratio: null,
					flat: null
				}
			})
		},

		/**
		 * product type we get from graphql policies.product
		 */
		product: {
			validator(val) {
				return val instanceof Object || val === null
			},
			required: true,
			default: () => ({
				name: null,
				productId: {
					plan: null,
					type: null,
					version: null
				}
			})
		},

		/**
		 * exclusions type we get from graphql policies.exclusions
		 */
		exclusions: {
			type: Array,
			required: true,
			default: () => [
				{
					id: null,
					codeName: {
						code: null,
						name: null
					},
					remark: null
				}
			]
		},

		benefitOptions: { type: Array }
	},

	data() {
		return {
			isDeleteLoading: false
		}
	},

	computed: {
		computedCreatedAt() {
			if (!this.createdAt) return ""
			return dayjs(this.createdAt).format(
				this.$store.state.settings.dateFormat
			)
		},

		computedBenefitOptions() {
			return this.benefitOptions
		}
	},

	methods: {
		async deleteOffer() {
			this.isDeleteLoading = true
			const res = await api.removeOffer({
				policyId: this.policyId,
				offerId: this.offerId
			})
			this.isDeleteLoading = false

			// Check errors
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				return
			}

			// Success
			this.$emit("deleted")
		}
	}
}
</script>